import React from 'react';

import constructSlug from '@/utils/construct-slug';
import { logTrackingEvent } from '@/utils/tracking';
import { convertToCurrencyFormat } from '@/utils/convert-to-currency-format';
import { PrimaryButton } from '@/components/button';
import StarRating from '@/components/star-rating';
import { H5, Small } from '@/components/type';
import { RATERS } from '@/constants';
import { STAR_PLACEMENT } from '@/components/star-rating/constants';

import {
  BottomContainer,
  CardWrapper,
  CarImage,
  CarImageWrapper,
  ModelDetails,
  ModelYear,
  PriceRangeDetails,
  Rating,
  TestDetails,
} from './styles';
import { type Rater } from '@/constants/raters';

type VehicleCardProps = {
  ANCAPTestYear?: number;
  gtmEvent?: string;
  image?: string;
  make: string;
  model: string;
  newPrice?: number;
  priceRange?: {
    low?: number;
    high?: number;
  };
  rating: {
    selectedRating: {
      rating?: number;
      provider?: Rater;
    };
  };
  recommendedSafetyFeatures: number;
  totalSafetyFeatures: number;
  year: number;
};

const VehicleCard = ({
  ANCAPTestYear,
  image = '/images/car-image-not-found.svg',
  make,
  model,
  newPrice,
  priceRange = { low: undefined, high: undefined },
  recommendedSafetyFeatures,
  totalSafetyFeatures,
  year,
  rating,
  gtmEvent,
}: VehicleCardProps) => {
  const linkToVehicle = `/${constructSlug(make)}/${constructSlug(model)}/${year}`;

  const { selectedRating } = rating;

  return (
    <CardWrapper href={linkToVehicle} passHref onClick={() => logTrackingEvent({ event: gtmEvent })}>
      <CarImageWrapper>
        <CarImage src={image} alt={`${year} ${make} ${model}`} />
      </CarImageWrapper>
      <ModelDetails>
        <H5>
          {make} {model}
        </H5>
        <ModelYear>{year}</ModelYear>
      </ModelDetails>
      <Rating>
        <StarRating
          stars={selectedRating.rating}
          ratingBy={selectedRating.provider}
          testYear={ANCAPTestYear}
          placement={STAR_PLACEMENT.CAR_CARD}
        />
        {selectedRating.provider === RATERS.NO_RATING && <Small>Not rated</Small>}
        {selectedRating.provider === RATERS.NO_CURRENT_RATING && <Small>No current rating</Small>}
      </Rating>
      <BottomContainer>
        <TestDetails>
          <Small>
            Recommended features{' '}
            <strong>
              {recommendedSafetyFeatures}/{totalSafetyFeatures}
            </strong>
          </Small>
        </TestDetails>
        <PriceRangeDetails>
          {priceRange.low && priceRange.high ? (
            <Small>
              From ${convertToCurrencyFormat(priceRange.low)} - ${convertToCurrencyFormat(priceRange.high)}
            </Small>
          ) : (
            <Small>{newPrice ? `Price Guide: $${convertToCurrencyFormat(newPrice)}` : 'Pricing unavailable'}</Small>
          )}
        </PriceRangeDetails>
        {/* Do not pass href to this link button since */}
        {/* the whole component is a <Link> and is clickable. */}
        <PrimaryButton fullWidth buttonAsDiv>
          More details
        </PrimaryButton>
      </BottomContainer>
    </CardWrapper>
  );
};

export default VehicleCard;
